<script lang="ts">
import { defineComponent } from 'vue';

let googleMapsPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if ((window as any).google?.maps?.places) return Promise.resolve();
  if (googleMapsPromise) return googleMapsPromise;

  const apiKey = (window as any).appConfig?.mapsApiKey;
  if (!apiKey) return Promise.resolve();

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-gmaps]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places`;
    script.async = true;
    script.setAttribute('data-gmaps', 'true');
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export default defineComponent({
  props: {
    placeId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isMapsLoaded: false,
    };
  },
  computed: {
    isValidPlaceId(): boolean {
      return (
        !!this.placeId &&
        this.placeId !== 'null' &&
        this.placeId.trim().length > 0
      );
    },
  },
  mounted() {
    if (!this.isValidPlaceId) {
      return;
    }

    if (this.$el instanceof HTMLElement && this.$el.offsetParent === null) {
      return;
    }

    if (
      typeof IntersectionObserver !== 'undefined' &&
      this.$el instanceof HTMLElement
    ) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.initMaps();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this.$el);
    } else {
      this.initMaps();
    }
  },
  methods: {
    async initMaps() {
      try {
        await loadGoogleMaps();
        this.isMapsLoaded = true;
      } catch (err) {
        console.warn('Google Maps could not be loaded', err);
      }
    },
  },
});
</script>

<template>
  <div v-if="isValidPlaceId" class="map-place-wrapper">
    <gmp-place-details-compact v-if="isMapsLoaded" orientation="horizontal">
      <gmp-place-details-place-request
        :place="placeId"
      ></gmp-place-details-place-request>
      <gmp-place-content-config>
        <gmp-place-rating></gmp-place-rating>
        <gmp-place-type></gmp-place-type>
        <gmp-place-attribution
          light-scheme-color="gray"
          dark-scheme-color="gray"
        ></gmp-place-attribution>
      </gmp-place-content-config>
    </gmp-place-details-compact>
  </div>
</template>

<style scoped lang="scss">
.map-place-wrapper {
  --gmp-mat-color-on-surface-variant: var(--text-color-dark);
  --gmp-mat-color-on-surface: var(--text-color-dark);
  --gmp-mat-color-primary: var(--color-gray-3);
  --gmp-mat-color-secondary-container: transparent;
  --gmp-mat-color-on-secondary-container: var(--color-gray-3);

  margin: 4px;

  gmp-place-details-compact {
    background-color: transparent;
    border: 0;
    color-scheme: only light;
    height: 90%;
    margin: 0 1px;
    position: relative;
    z-index: 2;
  }

  .reviews-wrapper {
    border: 0;
    height: 470px;
    margin-top: -10px;
    overflow: hidden;

    gmp-place-details {
      --gmp-mat-color-secondary-container: transparent;
      --gmp-mat-color-on-secondary-container: transparent;

      background-color: transparent;
      border: 0;
      color-scheme: only light;
      position: relative;
      top: -100px;
    }
  }
}
</style>
